import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "December 2025";

const SECTIONS = [
  {
    id: 1,
    title: "Information We Collect",
    intro:
      "To provide and manage our data-reward monetization framework, we gather specific categories of information:",
    items: [
      ["Account Registration", "Email addresses, usernames, and profile metadata provided when establishing an account."],
      ["Network & Device Telemetry", "Device IP addresses, hardware identifiers, operating system versions, and network speeds."],
      ["Data Transmission Metrics", "Aggregate volume of unutilized data monetized through the platform to accurately calculate payout balances."],
      ["Web Log Information", "Browser types, referral URLs, timestamps, and page engagement statistics."],
    ],
  },
  {
    id: 2,
    title: "How We Use Your Information",
    intro:
      "We use your collected metrics and personal data to optimize our platform and fulfill tokenomics transactions:",
    items: [
      ["Reward Distributions", "Processing financial balances, calculating digital earnings, and sending regular user payouts."],
      ["Network Optimization", "Improving data proxy systems, enhancing application security, and stabilizing platform connections."],
      ["Communications Management", "Sending direct account balances, service alerts, and responses to support queries."],
      ["Security & Fraud Protection", "Monitoring for proxy manipulations, multi-accounting violations, and suspicious network traffic."],
    ],
  },
  {
    id: 3,
    title: "Data Sharing and Disclosures",
    intro:
      "We never sell individual, identifiable user data to unauthorized parties. Information sharing is strictly confined to:",
    items: [
      ["Affiliated Routing Partners", "Aggregated, non-identifiable telemetry traffic shared to execute our data monetization networks."],
      ["Third-Party Service Providers", "Integrated secure payment gateway processors and cloud server infrastructure hosts."],
      ["Legal Enforcement Mandates", "Compliance requests under statutory disclosure obligations, judicial court orders, or governmental subpoenas."],
    ],
  },
  {
    id: 4,
    title: "Data Security & Storage",
    paragraphs: [
      "We utilize advanced industry-standard encryption, including transport layer security protocols and strict database access rules, to secure all incoming device telemetry data. We retain your account data for as long as your profile remains active or as required to fulfill legal tax and payout reporting requirements.",
    ],
  },
  {
    id: 5,
    title: "Cookies and Tracking Mechanisms",
    paragraphs: [
      "Our website incorporates functional cookies and persistent analytic tags to retain login preferences, evaluate site architecture performance, and smooth out standard dashboard processing operations. You can modify or deactivate tracking settings inside your native internet browser configurations at any time.",
    ],
  },
  {
    id: 6,
    title: "Your Global Privacy Rights",
    intro:
      "Depending on your geographic location (such as under the European Union GDPR or regional privacy acts), you may hold the following individual privileges:",
    items: [
      ["Access & Portability", "Requesting complete digital summaries of the precise personal information categories we store."],
      ["Correction", "Amending incomplete information or modifying invalid account registration credentials."],
      ["Erasure", "Demanding total permanent removal of your user profile and corresponding data stores."],
      ["Consent Withdrawal", "Revoking network tracking authorizations at any moment by closing your active account connection."],
    ],
  },
  {
    id: 7,
    title: "Policy Adjustments",
    paragraphs: [
      "We retain the right to modify this Privacy Policy document to mirror platform architecture upgrades or legal compliance adjustments. We will post all variations to this page with an adjusted date stamp.",
    ],
  },
  {
    id: 8,
    title: "Direct Contact Information",
    paragraphs: [
      "If you have any pending structural privacy complaints or data access inquiries, please reach out to us directly through the communication infrastructure provided on our website landing page.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="privacy-policy-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 antialiased selection:bg-[#4CAF50] selection:text-[#04140a]"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo-home" className="flex items-center">
            <img
              src="/geonetix-logo-full.webp"
              alt="GeoNetix"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 gx-grid-bg opacity-40" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#00B4D8]/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4"
          >
            / Legal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            Privacy <span className="gx-text-gradient">Policy</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-2xl text-slate-300 leading-relaxed"
          >
            At GeoNetix (accessible via{" "}
            <a
              href="https://www.geonetix.live/"
              className="text-[#00B4D8] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.geonetix.live/
            </a>
            , &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), your privacy is paramount. This Privacy Policy
            details how we collect, use, protect, and share your personal and
            data-usage metrics when you interact with our website and application
            services. By using our platform, you consent to the data practices
            outlined in this policy.
          </motion.p>
          <p className="mt-6 font-mono-gx text-xs uppercase tracking-[0.2em] text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-20">
        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`policy-section-${s.id}`}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-mono-gx text-2xl font-bold text-[#4CAF50]/70">
                  {String(s.id).padStart(2, "0")}
                </span>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                  {s.title}
                </h2>
              </div>

              {s.intro && (
                <p className="text-slate-400 leading-relaxed mb-5">{s.intro}</p>
              )}

              {s.paragraphs &&
                s.paragraphs.map((p, i) => (
                  <p key={i} className="text-slate-400 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

              {s.items && (
                <ul className="space-y-3">
                  {s.items.map(([term, desc], i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                    >
                      <span className="font-semibold text-white">{term}: </span>
                      <span className="text-slate-400">{desc}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-3xl border border-[#4CAF50]/25 bg-gradient-to-r from-[#4CAF50]/10 to-transparent p-8 text-center">
          <p className="text-slate-300 mb-6">
            Want to remove your account and personal data? You can request
            permanent deletion at any time.
          </p>
          <Link
            to="/data-deletion"
            data-testid="privacy-data-deletion-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#4CAF50]/50 px-7 py-3.5 text-sm font-semibold text-[#4CAF50] hover:bg-[#4CAF50]/10 transition-colors duration-200"
          >
            Request Account &amp; Data Deletion
          </Link>
        </div>

        <div className="mt-6 rounded-3xl border border-[#00B4D8]/25 bg-gradient-to-r from-[#00B4D8]/10 to-transparent p-8 text-center">
          <p className="text-slate-300 mb-6">
            Questions about your data? Head back to our homepage to reach us.
          </p>
          <Link
            to="/"
            data-testid="privacy-cta-home"
            className="inline-flex items-center gap-2 rounded-full bg-[#4CAF50] px-7 py-3.5 text-sm font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
          >
            <ArrowLeft size={16} /> Return to GeoNetix
          </Link>
        </div>
      </main>

      {/* Bottom bar */}
      <footer className="border-t border-white/10 bg-[#050d1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-9 w-auto object-contain" />
          <div className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} GeoNetix. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
